import React from 'react';
import { i18n } from 'config/locales';
import { CommonOnUpdate, CommonOnError } from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import classnames from 'classnames';
import { FeatureFlag } from 'components/FeatureFlags';
import { COUNTRY_FOREIGN } from 'constants/enums/locations';
import ErrorMessage from 'components/ErrorMessage';
import ValidationElement from '../ValidationElement';
import Show from '../Show';
import Dropdown from '../Dropdown';
import { internalTesting } from '../Dropdown/Dropdown';
import MultipleDropdown from '../MultipleDropdown';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';

const countries = i18n.value('countries');

interface Props extends ValidationElementProps {
  showComments?: boolean
  multiple?: boolean
  attribute?: string
  name: string
  label?: any
  placeholder?: any
  ariaLabel?: string
  disabled?: any
  required?: boolean
  value?: string | string[]
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  excludeUnitedStates?: boolean

  // When country is part of the location model and uses the Toggleable aspect,
  // this can set the value back to INTERNATIONAL when onUpdate returns falsy
  // once this prop is set to true.
  isPartOfLocationModel?: boolean
}

interface State {
  showComments: boolean
  focus: boolean
}

export default class Country extends ValidationElement<Props, State> {
  countries: React.RefObject<MultipleDropdown>;

  // This is different, as country has a stateFormErrorConnector and it is
  // messing with the type.
  country: React.RefObject<typeof internalTesting.Dropdown>;

  constructor(props: Props) {
    super(props);

    this.state = {
      showComments: props.showComments,
      focus: false,
    };

    this.countries = React.createRef();
    this.country = React.createRef();
  }

  update = (queue) => {
    this.props.onUpdate({
      name: this.props.name,
      showComments: this.props.showComments,
      value: this.props.value,
      focus: this.state.focus,
      ...queue,
    });
  };

  /**
   * Update function when there are multiple selectable countries
   */
  updateMultipleCountries = (values) => {
    const { value } = values;
    // should default to empty array if value is undefined/null/falsy
    this.update({ value: value || [] });
  };

  /**
   * Update function when there is only one country
   * @param {object} values - Country object
   */
  updateSingleCountry = (values) => {
    const { value } = values;
    let arr;
    let capitalizedCountry;
    if (value) {
      capitalizedCountry = value[0].toUpperCase() + value.slice(1);
    }
    const matchingCountries = this.filterValidCountries(value);
    if (matchingCountries.length > 0) {
      // If more than 1 matching country, keep typed value
      if (matchingCountries.length > 1) {
        arr = [capitalizedCountry];
      }
      // If only matched country, keep valid value
      if (matchingCountries.length === 1) {
        arr = [matchingCountries[0].value];
      }
    } else {
      arr = capitalizedCountry;
    }

    // When this is part of a location
    if (this.props.isPartOfLocationModel && !arr) { arr = 'INTERNATIONAL'; }

    this.update({ value: arr });
  };

  /**
   * Filters country names match anything in the country list
   * @param {string} country - Country Name
   */
  filterValidCountries = (country) => {
    const countryArray = Object.values(countries);
    return countryArray.filter((countryObj) => countryObj.value.toLowerCase() === country.toLowerCase());
  };

  handleError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      code: `country.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    // Determine if a `notfound` error is present and has a value of `false`.
    // When this happens we want to change the focus to the appropriate element
    // dependent on if this is singular or plural.
    const notfound = arr2.some((x) => x.valid === false && x.code.includes('notfound'));

    this.setState({ showComments: notfound });

    return this.props.onError(value, arr2);
  };

  /**
   * Handle the focus event.
   */
  handleFocus = (event) => {
    this.setState({ focus: true }, () => {
      this.props.updateCountryFocused(this.state.focus);
      super.handleFocus(event);
    });
  };

  /**
   * Handle the blur event.
   */
  handleBlur = (event) => {
    this.setState({ focus: false }, () => {
      this.props.updateCountryFocused(this.state.focus);
      super.handleBlur(event);
    });
  };

  validCountry(value): boolean {
    const options = Object.keys(countries).map((x) => (countries[x].value));
    return options.includes(value);
  }

  renderOptions = () => {
    const filter = this.props.excludeUnitedStates
      ? (x) => x !== 'unitedStates'
      : () => true;

    const countryOptions = Object.keys(countries)
      .filter(filter)
      .map((x) => (
        <option key={x} value={countries[x].value}>
          {countries[x].text}
        </option>
      ));

    // Check for children
    const children = this.props.children || [];
    const options = countryOptions.concat(
      children.map((x) => {
        if (x && x.type === 'option') {
          return x;
        }
        return null;
      })
    );

    if (this.props.placeholder) {
      return [
        <option key="placeholder" value="">
          {this.props.placeholder}
        </option>,
      ].concat(options);
    }

    return options;
  };

  appropriateValue = (value: Props['value'], multiple: Props['multiple'] = false) => {
    if (!value) {
      if (multiple) {
        return [];
      }
      return '';
    }

    // For the typical Dropdown component a string value is expected.
    // However, for the MultiDropdown, the value must be an array of objects.
    let simpleValue;
    const isArray = Array.isArray(value);
    if (multiple) {
      if (isArray) {
        simpleValue = value;
      } else {
        simpleValue = [value];
      }
    } else if (isArray) {
      simpleValue = value[0];
    } else {
      simpleValue = value;
    }

    return simpleValue;
  };

  render() {
    const className = classnames(['country', 'usa-form-control'], this.props.className);
    const options = this.renderOptions();
    const value = this.appropriateValue(this.props.value, this.props.multiple);

    return (
      <div>
        <Show when={this.props.multiple}>
          <MultipleDropdown
            attribute={this.props.attribute}
            name={this.props.name}
            ref={this.countries}
            label={this.props.label}
            placeholder={this.props.placeholder || 'Choose Country(ies). Selected items will appear below.'}
            className={className}
            ariaLabel={this.props.ariaLabel}
            disabled={this.props.disabled}
            onUpdate={this.updateMultipleCountries}
            onError={this.handleError}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            value={value}
            required={this.props.required}
            multiple={this.props.multiple}
            multipleLabel="Selected country(ies) citizenship"
          >
            {options}
          </MultipleDropdown>
        </Show>
        <Show when={!this.props.multiple}>
          {this.state.focus && value && !this.validCountry(value) && (
            <ErrorMessage
              isWarning
              title="This country is not on our current list"
              message={[
                "If you don't see your country in the list, select the country that presently governs the location you are referring to"
              ]}
            />
          )}
          <Dropdown
            attribute={this.props.attribute}
            name={this.props.name}
            ref={this.country}
            label={this.props.label}
            placeholder={this.props.placeholder}
            className={className}
            ariaLabel={this.props.ariaLabel}
            disabled={this.props.disabled}
            onUpdate={this.updateSingleCountry}
            onError={this.handleError}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            value={value === COUNTRY_FOREIGN ? '' : value}
            required={this.props.required}
          >
            {options}
          </Dropdown>
        </Show>

        {/* TODO: This needs to be moved to Field for error handling to match conformity.
        However, when state.form validation is live, this can be removed altogether. */}
        <FeatureFlag
          flagId="EN-4733"
          InactiveRender={() => (
            <Show when={this.state.showComments}>
              <div className="field no-margin-bottom">
                <div className="table">
                  <div className="usa-alert usa-alert-error" role="alert">
                    <div className="usa-alert-body">
                      <h5 className="usa-alert-heading">
                        {i18n.t('error.country.notfound.title')}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </Show>
          )}
        />
      </div>
    );
  }
}

Country.defaultProps = {
  name: 'country',
  value: [],
  focus: false,
  multiple: false,
  showComments: false,
  excludeUnitedStates: false,
  updateCountryFocused: (focus) => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
};

Country.errors = [];
